import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components as MarkdownComponents } from 'react-markdown';

const CancellationPolicyPage: React.FC = () => {
  const [content, setContent] = useState<string>('');
  const [subtitle, setSubtitle] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const markdownComponents: MarkdownComponents = {
    h1: ({ node, children, ...props }) => (
      <h1 className="heading-primary text-neutral-charcoal mt-8 mb-4 text-center" {...props}>{children}</h1>
    ),
    h2: ({ node, children, ...props }) => (
      <h2 className="heading-secondary text-primary-deep-blue mt-8 mb-3 text-center" {...props}>{children}</h2>
    ),
    h3: ({ node, children, ...props }) => (
      <h3 className="heading-tertiary text-primary-deep-blue mt-6 mb-2 text-center" {...props}>{children}</h3>
    ),
    p: ({ node, children, ...props }) => (
      <p className="body-large leading-8 text-neutral-dark-gray/90 mb-4" {...props}>{children}</p>
    ),
    ul: ({ node, children, ...props }) => (
      <ul className="list-disc pl-6 space-y-2 mb-4" {...props}>{children}</ul>
    ),
    ol: ({ node, children, ...props }) => (
      <ol className="list-decimal pl-6 space-y-2 mb-4" {...props}>{children}</ol>
    ),
    li: ({ node, children, ...props }) => (
      <li className="body-large leading-8 text-neutral-dark-gray/90" {...props}>{children}</li>
    ),
    strong: ({ node, children, ...props }) => (
      <strong className="font-semibold" {...props}>{children}</strong>
    ),
    em: ({ node, children, ...props }) => (
      <em className="italic" {...props}>{children}</em>
    ),
    a: ({ node, children, ...props }) => (
      <a className="text-primary-deep-blue underline" {...props}>{children}</a>
    ),
  };

  useEffect(() => {
    const fetchPolicy = async () => {
      try {
        const response = await fetch('/cancellation-policy/cancellation_policy.md', { cache: 'no-cache' });
        if (!response.ok) {
          throw new Error('Failed to load policy');
        }
        const text = await response.text();
        // Extract subtitle (intro paragraph) before the first H2 heading; render the rest as markdown
        const trimmed = text.trim();
        const firstH2Index = trimmed.search(/^##\s/m);
        if (firstH2Index > -1) {
          const intro = trimmed.slice(0, firstH2Index).trim();
          const rest = trimmed.slice(firstH2Index).trim();
          setSubtitle(intro);
          setContent(rest);
        } else {
          const blocks = trimmed.split(/\n\n+/);
          const intro = blocks[0]?.trim() || '';
          const rest = blocks.slice(1).join('\n\n').trim();
          setSubtitle(intro);
          setContent(rest);
        }
      } catch (err) {
        setError('Unable to load the policy at the moment. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchPolicy();
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="min-h-screen"
    >
      <section className="pt-28 pb-12 bg-neutral-pearl">
        <div className="container max-w-4xl">
          <h1 className="heading-primary text-neutral-charcoal text-center">Cancellation & Refund Policy</h1>
          {!isLoading && !error && subtitle && (
            <p className="body-large text-secondary-slate text-center mt-3 max-w-3xl mx-auto">{subtitle}</p>
          )}
        </div>
      </section>

      <section className="py-12 bg-neutral-white">
        <div className="container max-w-3xl">
          {isLoading && (
            <LoadingSpinner className="py-16" text="Loading policy..." />
          )}

          {!isLoading && error && (
            <div className="card p-6 body-regular text-error">{error}</div>
          )}

          {!isLoading && !error && (
            <article className="card card-luxury">
              <div className="card-content">
                <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkGfm]}>
                  {content}
                </ReactMarkdown>
              </div>
            </article>
          )}
        </div>
      </section>
    </motion.div>
  );
};

export default CancellationPolicyPage;


